import { configBaseId, poseDeCelda } from './celdas';
import type { CeldaEstado, Corrida, Evaluacion, TestSet } from './tipos';

/**
 * Alineación de dos corridas del MISMO set para la vista de comparación.
 * La identidad que une todo es el config_id estable dentro del set: las configs
 * presentes en ambas corridas se enfrentan fila por fila (variante); las que
 * están en una sola van marcadas soloEn y no se enfrentan. Puro, sin supabase.
 */

export interface LadoComparacion {
  /** Imágenes ENTREGADAS por esa celda, en orden. Vacío = falló o no llegó nada.
   *  Es un array porque el modo automático pide N en una sola llamada y las N
   *  comparten execution_id (ADR-2026-08-26-modo-automatico-en-el-test-masivo). */
  urls: string[];
  estado: CeldaEstado;
}

export interface FilaComparacion {
  variante_id: string;
  a: LadoComparacion | null;
  b: LadoComparacion | null;
}

export interface BloqueComparacion {
  config_id: string;
  nombre: string;
  filas: FilaComparacion[];
  soloEn?: 'A' | 'B';
}

function ladosDe(corrida: Corrida, configId: string, urls: Map<string, string[]>) {
  const porVariante = new Map<string, LadoComparacion>();
  for (const c of corrida.celdas) {
    if (c.config_id !== configId) continue;
    porVariante.set(c.variante_id, { urls: urls.get(c.execution_id) ?? [], estado: c.estado });
  }
  return porVariante;
}

export function alinearCorridas(
  set: TestSet,
  a: Corrida,
  b: Corrida,
  urls: Map<string, string[]>,
  /** id de pose (catálogo o custom) → nombre legible, para los bloques expandidos. */
  nombresPoses?: Map<string, string>,
): BloqueComparacion[] {
  const configsA = new Set(a.celdas.map((c) => c.config_id));
  const configsB = new Set(b.celdas.map((c) => c.config_id));
  const todas = [...new Set([...configsA, ...configsB])];
  const nombreDe = (id: string) => {
    const base = set.configs.find((c) => c.config_id === configBaseId(id))?.nombre ?? configBaseId(id).slice(0, 8);
    const pose = poseDeCelda(set, id);
    if (!pose) return base;
    const nombrePose = nombresPoses?.get(pose.id) ?? (pose.tipo === 'custom' ? 'pose propia' : pose.id.slice(0, 8));
    return `${base} · ${nombrePose}`;
  };

  const bloques: BloqueComparacion[] = todas.map((configId) => {
    const ladosA = configsA.has(configId) ? ladosDe(a, configId, urls) : null;
    const ladosB = configsB.has(configId) ? ladosDe(b, configId, urls) : null;
    const variantes = [...new Set([...(ladosA?.keys() ?? []), ...(ladosB?.keys() ?? [])])];
    const bloque: BloqueComparacion = {
      config_id: configId,
      nombre: nombreDe(configId),
      filas: variantes.map((v) => ({
        variante_id: v,
        a: ladosA?.get(v) ?? null,
        b: ladosB?.get(v) ?? null,
      })),
    };
    if (!ladosB) bloque.soloEn = 'A';
    else if (!ladosA) bloque.soloEn = 'B';
    return bloque;
  });

  // Los enfrentables primero; los soloEn al final.
  return bloques.sort((x, y) => Number(!!x.soloEn) - Number(!!y.soloEn));
}

export interface Marcador {
  A: number;
  B: number;
  empate: number;
}

export function marcador(evaluaciones: Evaluacion[]): {
  global: Marcador;
  porConfig: Map<string, Marcador>;
} {
  const global: Marcador = { A: 0, B: 0, empate: 0 };
  const porConfig = new Map<string, Marcador>();
  for (const ev of evaluaciones) {
    global[ev.ganador] += 1;
    const m = porConfig.get(ev.config_id) ?? { A: 0, B: 0, empate: 0 };
    m[ev.ganador] += 1;
    porConfig.set(ev.config_id, m);
  }
  return { global, porConfig };
}
